import React from 'react';
import Link from 'next/link';
import { shortDeviceName } from '../utils/deviceNameUtils';

interface Anomaly {
  id?: string;
  type: string;
  description: string;
  severity: string;
  timestamp: string;
  deviceId?: string;
  farmName?: string;
  acknowledged?: boolean;
  suggestion?: string;
}

interface AnomalyDisplayProps {
  anomalies: Anomaly[];
  maxItems?: number;
  showDeviceLink?: boolean;
  compact?: boolean;
  className?: string;
}

interface ParsedAnomaly {
  summary: string;
  details: string;
  advice: string;
}

// Parse anomaly description into structured parts for better visual presentation
const parseAnomalyDescription = (description: string): ParsedAnomaly => {
  if (!description.trim()) {
    return { summary: '', details: '', advice: '' };
  }

  // Simple approach: Split by sentence patterns and common delimiters
  const text = description.trim();
  
  // Try to identify three distinct parts by looking for common patterns
  let summary = '';
  let details = '';
  let advice = '';
  
  // Look for advice patterns first (most distinctive)
  const adviceMatch = text.match(/(.+?)\s+(Possible|Check|Consider|Recommend|May indicate|Suggest).+$/i);
  if (adviceMatch) {
    const beforeAdvice = adviceMatch[1].trim();
    advice = text.substring(beforeAdvice.length).trim();
    
    // Split the "before advice" part
    const detailsMatch = beforeAdvice.match(/(.+?)\s+\((\d+\s+out of\s+\d+)\)\.?\s*(.*)$/i);
    if (detailsMatch) {
      summary = detailsMatch[1].replace(/\.$/, '').trim();
      const countInfo = detailsMatch[2];
      const remaining = detailsMatch[3].trim();
      details = remaining ? `(${countInfo}). ${remaining}` : `(${countInfo})`;
    } else {
      // Look for other patterns like Dosing/Priming
      const dosingMatch = beforeAdvice.match(/(.+?)\s+(Dosing:.+)$/i);
      if (dosingMatch) {
        let rawSummary = dosingMatch[1].replace(/\.$/, '').trim();
        // If summary contains count info like "(18 out of 72)", extract just the main description
        const countMatch = rawSummary.match(/^(.+?)\s*\(\d+\s+out of\s+\d+\)(.*)$/);
        if (countMatch) {
          rawSummary = (countMatch[1] + countMatch[2]).trim();
        }
        summary = rawSummary;
        details = dosingMatch[2].trim();
      } else {
        // Split by sentence if multiple sentences
        const sentences = beforeAdvice.split(/\.\s+/);
        if (sentences.length > 1) {
          summary = sentences[0].trim();
          details = sentences.slice(1).join('. ').trim();
        } else {
          summary = beforeAdvice;
        }
      }
    }
  } else {
    // No advice found, try to split summary and details
    const detailsMatch = text.match(/(.+?)\s+(\d+\s+(?:events?|out of|Dosing:|Priming:).+)$/i);
    if (detailsMatch) {
      let rawSummary = detailsMatch[1].replace(/\.$/, '').trim();
      // If summary contains count info like "(18 out of 72)", extract just the main description
      const countMatch = rawSummary.match(/^(.+?)\s*\(\d+\s+out of\s+\d+\)(.*)$/);
      if (countMatch) {
        rawSummary = (countMatch[1] + countMatch[2]).trim();
      }
      summary = rawSummary;
      details = detailsMatch[2].trim();
    } else {
      // Split by sentences or use whole text as summary
      const sentences = text.split(/\.\s+/);
      if (sentences.length > 1) {
        summary = sentences[0].trim();
        const remaining = sentences.slice(1).join('. ').trim();
        
        // Decide if remaining is details or advice
        if (/^(Possible|Check|Consider|Recommend|May indicate|Suggest)/i.test(remaining)) {
          advice = remaining;
        } else {
          details = remaining;
        }
      } else {
        summary = text;
      }
    }
  }
  
  // Post-process summary to fix terminology issues
  // Replace "dosing events" with "events" to avoid confusion with specific "Dosing" event type
  const processedSummary = summary
    .replace(/\.$/, '')
    .replace(/dosing events/gi, 'events')
    .replace(/cancelled dosing events/gi, 'cancelled events')
    .replace(/multiple cancelled dosing events/gi, 'multiple cancelled events')
    .trim();
  
  return { 
    summary: processedSummary, 
    details: details.replace(/\.$/, '').trim(), 
    advice: advice.replace(/\.$/, '').trim()
  };
};

const getSeverityColor = (severity: string) => {
  switch (severity?.toLowerCase()) {
    case 'critical': return 'text-red-600 bg-red-50';
    case 'high': return 'text-orange-600 bg-orange-50';
    case 'medium': return 'text-yellow-600 bg-yellow-50';
    case 'low': return 'text-blue-600 bg-blue-50';
    case 'warning': return 'text-yellow-600 bg-yellow-50';
    default: return 'text-gray-600 bg-gray-50';
  }
};

const getSeverityBorderColor = (severity: string) => {
  switch (severity?.toLowerCase()) {
    case 'critical': return 'border-red-400';
    case 'high': return 'border-orange-400';
    case 'medium': return 'border-yellow-400';
    case 'low': return 'border-blue-400';
    case 'warning': return 'border-yellow-400';
    default: return 'border-gray-300';
  }
};

const getSeverityBackgroundColor = (severity: string) => {
  switch (severity?.toLowerCase()) {
    case 'critical': return 'bg-red-25';
    case 'high': return 'bg-orange-25';
    case 'medium': return 'bg-yellow-25';
    case 'low': return 'bg-blue-25';
    case 'warning': return 'bg-yellow-25';
    default: return 'bg-gray-25';
  }
};

const getSeverityIcon = (severity: string) => {
  switch (severity?.toLowerCase()) {
    case 'critical': return '🚨';
    case 'high': return '⚠️';
    case 'medium': return '⚡';
    case 'low': return 'ℹ️';
    case 'warning': return '⚠️';
    default: return '📋';
  }
};

const formatTimestamp = (timestamp: string) => {
  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  } catch {
    return 'Unknown time';
  }
};

export const AnomalyDisplay: React.FC<AnomalyDisplayProps> = ({ 
  anomalies, 
  maxItems, 
  showDeviceLink = false, 
  compact = false,
  className = '' 
}) => {
  if (!anomalies || anomalies.length === 0) {
    return null;
  }

  const displayedAnomalies = maxItems ? anomalies.slice(0, maxItems) : anomalies;
  const hasMoreAnomalies = maxItems && anomalies.length > maxItems;

  if (compact) {
    // Compact view for main dashboard
    return (
      <div className={`space-y-3 ${className}`}>
        {displayedAnomalies.map((anomaly, index) => {
          const parsedAnomaly = parseAnomalyDescription(anomaly.description);
          return (
            <div key={anomaly.id || `anomaly-${index}`} className={`border-l-4 pl-3 py-2 rounded-r ${getSeverityBorderColor(anomaly.severity)} ${getSeverityBackgroundColor(anomaly.severity)}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(anomaly.severity)}`}>
                    {getSeverityIcon(anomaly.severity)}
                    {anomaly.type || 'System Alert'}
                  </span>
                  <span className="text-xs text-gray-500">
                    {formatTimestamp(anomaly.timestamp)}
                  </span>
                </div>
                {showDeviceLink && anomaly.deviceId && (
                  <Link 
                    href={`/device/${anomaly.deviceId}`}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    {shortDeviceName(anomaly.farmName || `Device ${anomaly.deviceId}`)} →
                  </Link>
                )}
              </div>

              {/* Detection Summary */}
              {parsedAnomaly.summary && (
                <div className="mb-2">
                  <div className="text-sm font-medium text-gray-900 leading-tight">
                    {parsedAnomaly.summary}
                  </div>
                </div>
              )}

              {/* Event Details */}
              {parsedAnomaly.details && (
                <div className="mb-2">
                  <div className="text-xs text-gray-700 bg-gray-50 rounded px-2 py-1 font-mono">
                    📊 {parsedAnomaly.details}
                  </div>
                </div>
              )}

              {/* Diagnosis/Advice — backend suggestion takes priority over parsed advice */}
              {(anomaly.suggestion || parsedAnomaly.advice) && (
                <div>
                  {anomaly.suggestion ? (
                    <div className="text-xs text-emerald-700 bg-emerald-50 rounded px-2 py-1 flex items-start gap-1">
                      <span className="text-emerald-500 mt-0.5">🔧</span>
                      <span className="font-medium">{anomaly.suggestion}</span>
                    </div>
                  ) : (
                    <div className="text-xs text-blue-700 bg-blue-50 rounded px-2 py-1 flex items-start gap-1">
                      <span className="text-blue-500 mt-0.5">💡</span>
                      <span className="italic font-medium">{parsedAnomaly.advice}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Fallback for unparsed descriptions */}
              {!parsedAnomaly.summary && !parsedAnomaly.details && !parsedAnomaly.advice && !anomaly.suggestion && (
                <div className="text-sm text-gray-800">
                  {anomaly.description}
                </div>
              )}
            </div>
          );
        })}
        
        {hasMoreAnomalies && (
          <div className="text-xs text-gray-500 text-center py-2 border-t border-gray-100">
            +{anomalies.length - maxItems!} more issues • 
            <Link href="/attention" className="text-blue-600 hover:text-blue-800">
              View all →
            </Link>
          </div>
        )}
      </div>
    );
  }

  // Full detailed view for attention page and device details
  // Check if this is the attention page view for enhanced spacing
  const isAttentionView = className?.includes('anomaly-attention-view');
  
  return (
    <div className={`space-y-4 ${className}`}>
      {displayedAnomalies.map((anomaly, index) => {
        const parsedAnomaly = parseAnomalyDescription(anomaly.description);
        return (
          <div key={anomaly.id || `anomaly-${index}`} className={`border-l-4 pl-4 py-3 rounded-r ${getSeverityBorderColor(anomaly.severity)} ${getSeverityBackgroundColor(anomaly.severity)}`}>
            {/* Anomaly Header */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(anomaly.severity)}`}>
                  {getSeverityIcon(anomaly.severity)}
                  {anomaly.type || 'System Alert'}
                </span>
                <span className="text-xs text-gray-500">
                  {formatTimestamp(anomaly.timestamp)}
                </span>
              </div>
              {showDeviceLink && anomaly.deviceId && (
                <Link 
                  href={`/device/${anomaly.deviceId}`}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  {shortDeviceName(anomaly.farmName || `Device ${anomaly.deviceId}`)} →
                </Link>
              )}
            </div>

            {/* Detection Summary */}
            {parsedAnomaly.summary && (
              <div className="mb-3">
                <div className={`font-medium text-gray-900 leading-tight ${
                  isAttentionView ? 'text-sm lg:text-base' : 'text-sm'
                }`}>
                  {parsedAnomaly.summary}
                </div>
              </div>
            )}

            {/* Event Details */}
            {parsedAnomaly.details && (
              <div className="mb-3">
                <div className={`text-gray-700 bg-gray-50 rounded font-mono ${
                  isAttentionView ? 'text-xs lg:text-sm px-3 py-2.5' : 'text-xs px-3 py-2'
                } break-words`}>
                  📊 {parsedAnomaly.details}
                </div>
              </div>
            )}

            {/* Diagnosis/Advice — backend suggestion takes priority over parsed advice */}
            {(anomaly.suggestion || parsedAnomaly.advice) && (
              <div>
                {anomaly.suggestion ? (
                  <div className={`text-emerald-700 bg-emerald-50 rounded flex items-start gap-2 break-words ${
                    isAttentionView ? 'text-xs lg:text-sm px-3 py-2.5' : 'text-xs px-3 py-2'
                  }`}>
                    <span className="text-emerald-500 mt-0.5 flex-shrink-0">🔧</span>
                    <span className="font-medium">{anomaly.suggestion}</span>
                  </div>
                ) : (
                  <div className={`text-blue-700 bg-blue-50 rounded flex items-start gap-2 break-words ${
                    isAttentionView ? 'text-xs lg:text-sm px-3 py-2.5' : 'text-xs px-3 py-2'
                  }`}>
                    <span className="text-blue-500 mt-0.5 flex-shrink-0">💡</span>
                    <span className="italic font-medium">{parsedAnomaly.advice}</span>
                  </div>
                )}
              </div>
            )}

            {/* Fallback for unparsed descriptions */}
            {!parsedAnomaly.summary && !parsedAnomaly.details && !parsedAnomaly.advice && !anomaly.suggestion && (
              <div className={`text-gray-800 leading-relaxed break-words ${
                isAttentionView ? 'text-sm lg:text-base' : 'text-sm'
              }`}>
                {anomaly.description}
              </div>
            )}
          </div>
        );
      })}
      
      {hasMoreAnomalies && (
        <div className="text-sm text-gray-500 text-center py-3 border-t border-gray-200">
          Showing {maxItems} of {anomalies.length} issues • 
          {showDeviceLink ? (
            <Link href="/attention" className="text-blue-600 hover:text-blue-800 font-medium">
              View all issues →
            </Link>
          ) : (
            <span className="font-medium">Scroll up to see more</span>
          )}
        </div>
      )}
    </div>
  );
};

export default AnomalyDisplay;